// =============================================================================
// Widget bodies that fetch their own data.
//
// Fuel, Nachhilfe and Projekte do not live in the shared `data` tree — they
// talk to Supabase through their lib modules. These bodies therefore load
// once on mount (and again only when `scope` changes); they never poll, so
// they add no recurring request load on top of the app's 5s data sync.
//
// Every body follows the standard contract:
//   { data, updateData, scope, size, today, t, lang }
// and must never throw: each lib call is wrapped, failures render an empty
// state and a console.warn.
// =============================================================================

import React, { useState, useEffect } from 'react';
import { parseISO } from 'date-fns';
import { formatDate as fmt } from '../../../lib/dates';
import { Fuel, GraduationCap, Wallet, Clock } from 'lucide-react';

import { listFuelLogs, listFuelEntries, computeBalances } from '../../../lib/fuelLogs';
import {
  listStudents,
  listSessions,
  listPayments,
  computeStudentBalance
} from '../../../lib/tutoring';
import {
  listProjects,
  listAllTransactions,
  summarise as summariseProject
} from '../../../lib/projects';

import { StatTile, WidgetEmpty, WidgetList, WidgetRow, formatEUR, formatHours, isCompact } from '../shared';

// -----------------------------------------------------------------------------
// Loading placeholder — deliberately quiet, no spinner.
// -----------------------------------------------------------------------------
const WidgetLoading = () => (
  <div className="h-full w-full flex items-center justify-center px-4 py-6">
    <span className="text-sm text-gray-300 dark:text-gray-600">…</span>
  </div>
);

// Runs `load()` once per `key` change. `load` is intentionally excluded from
// the dependency array (it is re-created every render) — `key` is the single
// source of truth for "fetch again", which keeps this a mount-only fetch.
const useAsyncOnce = (load, key) => {
  const [state, setState] = useState({ status: 'loading', value: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', value: null });
    (async () => {
      try {
        const value = await load();
        if (!cancelled) setState({ status: 'ready', value });
      } catch (err) {
        console.warn('[dashboard] widget data load failed', err);
        if (!cancelled) setState({ status: 'error', value: null });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
};

// German short date for a YYYY-MM-DD string; '—' when unparseable.
const fmtShortDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return fmt(parseISO(dateStr), 'short');
  } catch (_) {
    return null;
  }
};

// Money in the log's own currency (mirrors fmtMoney in FuelLog.js).
const fmtMoney = (n, currency = 'EUR') => {
  if (n == null || !Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 2
    }).format(n);
  } catch (_) {
    return formatEUR(n);
  }
};

const fmtKm = (n) =>
  Number.isFinite(n) ? `${new Intl.NumberFormat('de-DE').format(Math.round(n))} km` : '—';

// =============================================================================
// fuel.consumption — headline number of one fuel log.
//
// fuelLogs.js has no L/100km derivation (liters are optional per entry and
// the lib's math is purely km/cost settlement), so the headline is the last
// fill-up's price — straight out of computeBalances().perFillUp — falling
// back to the log's total km when no fill-up has been recorded yet.
// =============================================================================
const FuelConsumption = ({ scope, size, t }) => {
  const load = async () => {
    const logs = await listFuelLogs();
    if (!logs.length) return { empty: true };
    const log = logs.find((l) => l.id === scope) || logs[0];
    const entries = await listFuelEntries(log.id);
    if (!entries.length) return { empty: true, log };
    const balances = computeBalances(entries, []);
    return { log, entries, balances };
  };

  const { status, value } = useAsyncOnce(load, String(scope || 'default'));
  const compact = isCompact(size);

  if (status === 'loading') return <WidgetLoading />;
  if (status === 'error') {
    return <WidgetEmpty>{t('dashw.fuel.error', 'Tankdaten nicht geladen')}</WidgetEmpty>;
  }
  if (!value || value.empty) {
    return <WidgetEmpty>{t('dashw.fuel.empty', 'Noch keine Tankeinträge')}</WidgetEmpty>;
  }

  const { log, entries, balances } = value;
  const perFillUp = balances.perFillUp || [];
  const lastFillUp = perFillUp.length ? perFillUp[perFillUp.length - 1] : null;

  // Newest entry by date (entries arrive in odometer order).
  const lastEntry = entries.reduce(
    (best, e) =>
      !best || String(e.entryDate).localeCompare(String(best.entryDate)) > 0 ? e : best,
    null
  );
  const lastDate = fmtShortDate(lastEntry && lastEntry.entryDate);

  const headline = lastFillUp
    ? fmtMoney(lastFillUp.totalCost, log.currency)
    : fmtKm(balances.totalKm);
  const label = lastFillUp
    ? t('dashw.fuel.lastFillUp', 'Letzter Tankstopp')
    : t('dashw.fuel.totalKm', 'Gefahrene km');

  return (
    <StatTile
      value={headline}
      label={label}
      sub={lastDate ? t('dashw.fuel.lastEntry', 'Letzter Eintrag {date}', { date: lastDate }) : log.name}
      tone="default"
      icon={Fuel}
      compact={compact}
    />
  );
};

// =============================================================================
// tutoring.open — total still owed to me across active students.
//
// Mirrors Tutoring.js exactly: computeStudentBalance() per non-archived
// student (payments minus unpaid sessions; sessions flagged `paid` never
// enter the balance), then the sum of the negative balances. Credits from
// prepaying parents deliberately do not offset other students' debts.
// =============================================================================
const TutoringOpen = ({ size, t }) => {
  const load = async () => {
    const [students, sessions, payments] = await Promise.all([
      listStudents({ includeArchived: true }),
      listSessions(),
      listPayments()
    ]);
    return { students, sessions, payments };
  };

  const { status, value } = useAsyncOnce(load, 'tutoring');
  const compact = isCompact(size);

  if (status === 'loading') return <WidgetLoading />;
  if (status === 'error') {
    return <WidgetEmpty>{t('dashw.tutoring.error', 'Nachhilfe nicht geladen')}</WidgetEmpty>;
  }

  const students = (value && value.students) || [];
  const active = students.filter((s) => !s.archived);
  if (!active.length) {
    return <WidgetEmpty>{t('dashw.tutoring.empty', 'Noch keine Schüler')}</WidgetEmpty>;
  }

  const sessions = (value && value.sessions) || [];
  const payments = (value && value.payments) || [];

  let totalOwed = 0;
  let owingCount = 0;
  for (const stu of active) {
    const { balance } = computeStudentBalance(
      sessions.filter((s) => s.studentId === stu.id),
      payments.filter((p) => p.studentId === stu.id)
    );
    if (balance < 0) {
      totalOwed += -balance;
      owingCount += 1;
    }
  }

  const sub =
    owingCount === 0
      ? t('dashw.tutoring.allSettled', 'Alles bezahlt')
      : owingCount === 1
      ? t('dashw.tutoring.oneStudent', '1 Schüler offen')
      : t('dashw.tutoring.nStudents', '{count} Schüler offen', { count: owingCount });

  return (
    <StatTile
      value={formatEUR(totalOwed)}
      label={t('dashw.tutoring.open', 'Offene Beträge')}
      sub={sub}
      tone={totalOwed > 0 ? 'negative' : 'positive'}
      icon={GraduationCap}
      compact={compact}
    />
  );
};

// =============================================================================
// projects.overview — one project's headline, or a short list of all of them.
//
// 'profit' projects use projects.js summarise().netProfit. 'time' projects
// carry no transactions at all (Projects.js: "Erfasse Zeit über den Time
// Tracker"), so their hours come from data.timetracker.entries tagged with
// the project id — the same start/end arithmetic TimeTracker.js uses.
// =============================================================================
const trackedSeconds = (data, projectId, now) => {
  const entries = (data && data.timetracker && data.timetracker.entries) || [];
  const nowMs = now instanceof Date ? now.getTime() : Date.now();
  let total = 0;
  for (const e of entries) {
    if (!e || e.projectId !== projectId || !e.startedAt) continue;
    const start = Date.parse(e.startedAt);
    if (!Number.isFinite(start)) continue;
    const end = e.endedAt ? Date.parse(e.endedAt) : nowMs;
    if (!Number.isFinite(end)) continue;
    total += Math.max(0, Math.floor((end - start) / 1000));
  }
  return total;
};

const netTone = (net) => {
  if (net > 0) return 'positive';
  if (net < 0) return 'negative';
  return 'default';
};

const ProjectsOverview = ({ data, scope, size, today, t }) => {
  const load = async () => {
    const [projects, txns] = await Promise.all([listProjects(), listAllTransactions()]);
    return { projects, txns };
  };

  const { status, value } = useAsyncOnce(load, String(scope || 'all'));
  const compact = isCompact(size);

  if (status === 'loading') return <WidgetLoading />;
  if (status === 'error') {
    return <WidgetEmpty>{t('dashw.projects.error', 'Projekte nicht geladen')}</WidgetEmpty>;
  }

  const all = (value && value.projects) || [];
  const txns = (value && value.txns) || [];
  const projects = all.filter((p) => !p.archived);
  if (!projects.length) {
    return <WidgetEmpty>{t('dashw.projects.empty', 'Noch keine Projekte')}</WidgetEmpty>;
  }

  // Headline number + tone for one project, using the lib's own summarise().
  const headlineFor = (project) => {
    if (project.kind === 'time') {
      const seconds = trackedSeconds(data, project.id, today);
      return { text: formatHours(seconds), tone: 'default', isTime: true, count: 0 };
    }
    const s = summariseProject(txns.filter((x) => x.projectId === project.id));
    return {
      text: `${s.netProfit > 0 ? '+' : ''}${formatEUR(s.netProfit)}`,
      tone: netTone(s.netProfit),
      isTime: false,
      count: s.count
    };
  };

  // ---- single project -------------------------------------------------------
  if (scope && scope !== 'all') {
    const project = projects.find((p) => p.id === scope);
    if (!project) {
      return <WidgetEmpty>{t('dashw.projects.missing', 'Projekt nicht gefunden')}</WidgetEmpty>;
    }
    const h = headlineFor(project);
    const sub = h.isTime
      ? t('dashw.projects.timeProject', 'Zeit-Projekt')
      : h.count === 1
      ? t('dashw.projects.oneEntry', '1 Eintrag')
      : t('dashw.projects.nEntries', '{count} Einträge', { count: h.count });
    return (
      <StatTile
        value={h.text}
        label={project.name}
        sub={sub}
        tone={h.tone}
        icon={h.isTime ? Clock : Wallet}
        compact={compact}
      />
    );
  }

  // ---- all projects ---------------------------------------------------------
  const rows = projects.slice(0, compact ? 3 : 6);
  const toneClass = {
    positive: 'text-green-600 dark:text-green-400',
    negative: 'text-red-600 dark:text-red-400',
    default: 'text-gray-900 dark:text-gray-100'
  };

  return (
    <WidgetList>
      {rows.map((p) => {
        const h = headlineFor(p);
        return (
          <WidgetRow key={p.id}>
            {h.isTime ? (
              <Clock size={14} className="text-gray-400 flex-shrink-0" />
            ) : (
              <Wallet size={14} className="text-gray-400 flex-shrink-0" />
            )}
            <span className="flex-1 min-w-0 truncate text-sm text-gray-900 dark:text-gray-100">
              {p.name}
            </span>
            <span
              className={`text-sm font-medium tabular-nums flex-shrink-0 ${toneClass[h.tone]}`}
            >
              {h.text}
            </span>
          </WidgetRow>
        );
      })}
    </WidgetList>
  );
};

export const ASYNC_BODIES = {
  'fuel.consumption': FuelConsumption,
  'tutoring.open': TutoringOpen,
  'projects.overview': ProjectsOverview
};
